/**
 * 🧹 DATA CLEANUP PAGE
 * Admin tool to clean up user data for testing/fresh signups
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider,
  Stack,
} from '@mui/material';
import {
  Delete,
  Warning,
  CheckCircle,
  ExpandMore,
  Code,
  Storage,
  DataObject,
  ContentCopy,
} from '@mui/icons-material';

import { 
  clearUserLocalStorageData, 
  clearUserSessionStorageData, 
  clearAllBrowserData,
  generateBackendCleanupCommands,
  type DataCleanupResult 
} from '@/utils/dataCleanup';

export const DataCleanupPage: React.FC = () => {
  const [email, setEmail] = useState('olayemss.me@gmail.com');
  const [results, setResults] = useState<DataCleanupResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showBackendCommands, setShowBackendCommands] = useState(false);

  const handleCleanupUser = async () => {
    if (!email.trim()) {
      alert('Please enter an email address');
      return;
    }

    setLoading(true);
    const cleanupResults: DataCleanupResult[] = [];

    try {
      // Clear localStorage
      const localResult = clearUserLocalStorageData(email);
      cleanupResults.push({ ...localResult, clearedItems: localResult.clearedItems.map(item => `🗂️ ${item}`) });

      // Clear sessionStorage
      const sessionResult = clearUserSessionStorageData(email);
      cleanupResults.push({ ...sessionResult, clearedItems: sessionResult.clearedItems.map(item => `💾 ${item}`) });

      setResults(cleanupResults);
      setShowBackendCommands(true);

    } catch (error) {
      console.error('Cleanup failed:', error);
      setResults([{
        success: false,
        clearedItems: [],
        errors: [error instanceof Error ? error.message : 'Unknown error']
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAllData = () => {
    if (!confirm('⚠️ This will clear ALL browser storage data. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      const result = clearAllBrowserData();
      setResults([result]);
    } catch (error) {
      console.error('Full cleanup failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const backendCommands = generateBackendCleanupCommands(email);

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          🧹 Data Cleanup Tool
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Clean up user data for fresh signup testing
        </Typography>
      </Box>

      {/* Cleanup Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            User Data Cleanup
          </Typography>
          
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="User Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email to clean up"
              helperText="This will clear frontend localStorage and sessionStorage for this email"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<Delete />}
                onClick={handleCleanupUser}
                disabled={loading}
                color="warning"
              >
                {loading ? 'Cleaning...' : 'Clean User Data'}
              </Button>

              <Button
                variant="outlined"
                startIcon={<Warning />}
                onClick={handleClearAllData}
                disabled={loading}
                color="error"
              >
                Clear ALL Storage
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Results */}
      {results.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Cleanup Results
            </Typography>

            {results.map((result, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                {result.success ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Frontend cleanup completed successfully!
                  </Alert>
                ) : (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Cleanup failed: {result.errors?.join(', ')}
                  </Alert>
                )}

                {result.clearedItems.length > 0 && (
                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Cleared Items ({result.clearedItems.length}):
                    </Typography>
                    <List dense>
                      {result.clearedItems.map((item, i) => (
                        <ListItem key={i}>
                          <ListItemIcon>
                            <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={item}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Backend Cleanup Commands */}
      {showBackendCommands && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <DataObject sx={{ mr: 1 }} />
              <Typography variant="h6">
                Backend Cleanup Required
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Frontend data cleared!</strong> Now you need to clean up the backend database.
                Share these SQL commands with your backend developer:
              </Typography>
            </Alert>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Code />
                  <Typography>SQL Commands for Backend Cleanup</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Paper sx={{ p: 2, backgroundColor: 'grey.900', color: 'white', position: 'relative' }}>
                  <Button
                    size="small"
                    startIcon={<ContentCopy />}
                    onClick={() => copyToClipboard(backendCommands.join('\n'))}
                    sx={{ 
                      position: 'absolute', 
                      top: 8, 
                      right: 8, 
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                    }}
                  >
                    Copy
                  </Button>
                  <pre style={{ margin: 0, fontSize: '0.8rem', lineHeight: 1.4, paddingRight: '80px' }}>
                    {backendCommands.join('\n')}
                  </pre>
                </Paper>
                
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    ⚠️ <strong>DANGEROUS:</strong> These commands will permanently delete all user data. 
                    Make sure you have the correct user ID before running them on production.
                  </Typography>
                </Alert>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};